import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { TimeHlp } from 'src/lib/fliko/helpers/time.helper';
import { CompanyData, CompanyWebTheme } from 'src/lib/fliko/models/company/company.data';
import { CompanyEmployeeData, EmployeeData } from 'src/lib/fliko/models/company/employee.data';
import { EstateData, EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { UserAccessData, UserEstateAccessData } from 'src/lib/fliko/models/users/access.data';
import { UserData, UserType } from 'src/lib/fliko/models/users/user.data';
import { Nullable } from 'src/lib/fliko/types/typeish.type';
import { AsyncHlp } from 'src/app/shared/helpers/async.helper';
import { ViewModelHelper } from 'src/app/shared/helpers/viewModel.helper';
import { AuthService } from 'src/app/shared/services/auth.service';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';
import { CompanyService } from 'src/app/view-company/service/company.service';
import { AdminService } from 'src/app/view/admin/services/admin.service';
import { EstateService } from 'src/app/view/estate-view/services/estate.service';
import { VersionNumber } from 'src/app/view/help/services/models/versionInfo.data';
import { VersionHistoryService } from 'src/app/view/help/services/version-history-data.service';
import { UserService } from 'src/app/view/users/services/user.service';
import { RenewPasswordComponent } from '../components/renew-password/renew-password.component';
import { WhatsNewComponent } from '../components/whats-new/whats-new.component';
import { AppThemeService } from './app-theme.service';
import { ContextService } from './context.service';
import { BreakData } from './models/break.data';
import { ContextPermVM } from './models/context-perm.view-model';
import { ContextType } from './models/context.data';
import { EstateUserVM } from './models/estateUser.data';

@Injectable({ providedIn: 'root' })
export class ContextDataService implements OnDestroy {
  private _estate$ = new BehaviorSubject<EstateData | null>(null);
  private _user$ = new BehaviorSubject<UserData | null>(null);
  private _company$ = new BehaviorSubject<CompanyData | null>(null);
  private _compEmployee$ = new BehaviorSubject<CompanyEmployeeData | null>(null);
  private _estateAccess$ = new BehaviorSubject<string[]>([]);
  private _superAdmins$ = new BehaviorSubject<string[]>([]);
  private _theme$ = new BehaviorSubject<CompanyWebTheme | null>(null);
  private _adminUsers$ = new BehaviorSubject<UserData[]>([]);
  private _contextPerms$ = new BehaviorSubject<ContextPermVM | null>(null);
  private _appBreak$ = new BehaviorSubject<BreakData | null>(null);
  private _context$ = new BehaviorSubject<ContextType | null>(null);
  private _version$ = new BehaviorSubject<VersionNumber>('0.0.0');

  // ? All users in current estate (resident users + estate admins)
  private _usersInEstate$ = new BehaviorSubject<UserData[]>([]);
  private _estateUsersVM$ = new BehaviorSubject<EstateUserVM[]>([]);

  private _superAdmin = false;

  private subscription$ = new Subscription();
  private userIdSub$ = new Subscription();

  constructor(
    private adminService: AdminService,
    private companyService: CompanyService,
    private ctxSrv: ContextService,
    private dbSrv: DatabaseService,
    private estateService: EstateService,
    private ls: LocalizationService,
    private userService: UserService,
    private appThemeService: AppThemeService,
    private sessionStorageSrv: SessionStorageService,
    private dialogSrv: DialogService,
    private versionSrv: VersionHistoryService,
    private authSrv: AuthService,
    @Optional() @SkipSelf() thisService?: ContextDataService
  ) {
    if (thisService) throw new Error('Multiple instances of ContextDataService');
    this.userIdSub$ = this.ctxSrv
      .getUserId$()
      .pipe(distinctUntilChanged())
      .subscribe((userId) => {
        this.subscription$.unsubscribe();
        this.subscription$ = new Subscription();
        this.clearContext();
        if (userId) this.init();
      });
  }

  private clearContext(): void {
    this._contextPerms$.next(null);
    this._context$.next(null);
    this._estate$.next(null);
    this._company$.next(null);
    this._user$.next(null);
    this._compEmployee$.next(null);
    this._estateAccess$.next([]);
    this._superAdmins$.next([]);
    this._theme$.next(null);
    this._adminUsers$.next([]);
    this._usersInEstate$.next([]);
    this._estateUsersVM$.next([]);
    this._appBreak$.next(null);
    this._version$.next('0.0.0');
    this._superAdmin = false;
    this.appThemeService.setTheme();
  }

  private init(): void {
    this.subscription$.add(this.ctxSrv.getContextType$().subscribe((ctx) => this._context$.next(ctx)));

    this.subscription$.add(
      this.ctxSrv
        .getEstateId$()
        .pipe(
          switchMap((estateId) =>
            estateId
              ? combineLatest([ContextService.getEstateObject(this.ctxSrv)(estateId).get(), this.dbEstateUser.getMap()])
              : of([])
          )
        )
        .subscribe(([estate, estateAccess]) => {
          this._estate$.next(estate ?? null);

          if (estateAccess) {
            const estateUsersVM: EstateUserVM[] = [];
            // ! EstateId not necessary for this application
            estateAccess.forEach((userAccess, userId) =>
              estateUsersVM.push({
                id: userId,
                accesses: Object.values(userAccess).map((a) => ViewModelHelper.unitAccessToVM(a, '', this.ls))
              })
            );
            this._estateUsersVM$.next(estateUsersVM);
          } else {
            this._estateUsersVM$.next([]);
          }
        })
    );

    this.subscription$.add(
      this.ctxSrv
        .getCompanyId$()
        .pipe(
          switchMap((companyId) =>
            companyId ? combineLatest([this.companyService.getTheme$(companyId), this.dbCompany(companyId).get()]) : of([])
          )
        )
        .subscribe(([theme, company]) => {
          this.appThemeService.setTheme(theme?.web?.cssVars);
          this._theme$.next(theme?.web ?? null);
          this._company$.next(company ?? null);
        })
    );

    this.subscription$.add(
      this.ctxSrv
        .getUserId$()
        .pipe(
          switchMap((userId) => {
            if (userId) {
              return combineLatest([
                this.userService.getUser$(userId),
                this.adminService.getUserCRMAdminAccess$(userId),
                combineLatest([this.dbSuperadmin.getList(), this.dbAppBreak.get(), this.dbFlikoVersion.get()]),
                this.companyService.getEmployee$(userId),
                this.companyService.getCompanyEmployee$(userId)
              ]);
            } else return of([]);
          })
        )
        .subscribe(([user, crmAccess, [superAdmins, appBreak, version], employee, compEmployee]) => {
          const lastUserId = this._user$.value?.id ?? null;
          this._appBreak$.next(appBreak ?? null);
          this._version$.next(version ?? '0.0.0');
          this._user$.next(user ?? null);
          this._compEmployee$.next(compEmployee ?? null);
          // ? JavaScript object keys are always coerced to a string, so obj[0] is always the same as obj["0"]
          this._estateAccess$.next(
            (ObjHlp.keys(crmAccess?.estates) as string[]).filter((estateId) => crmAccess?.estates?.[estateId] === true)
          );
          this._superAdmins$.next(superAdmins ?? []);
          this._superAdmin = !!superAdmins?.includes(user?.id ?? '');

          if (user) {
            const lang = user.language;
            const storLang = this.sessionStorageSrv.locale.get();
            if (lang && lang !== storLang) {
              this.ls.currentLang = lang;
            }

            this.sessionStorageSrv.lastSignIn.set((user.lastActivity ?? 0).toString());

            if (user.id !== lastUserId) this.handleUserChange(user, employee);
          }
        })
    );

    this.subscription$.add(
      combineLatest([
        this._estate$,
        this.userService.getUsers$(),
        this.adminService.getCRMAdminAccessMap$(),
        this.adminService.getEstateUserAccessMap$()
      ]).subscribe(([estate, allUsers, adminsMap, estateUserAccessMap]) => {
        const adminUsers: UserData[] = [];
        const usersInEstate: UserData[] = [];

        allUsers.forEach((user) => {
          let userInEstate = false;
          // ? estateAdmins does not have estateUserAccess until they are also residents
          if (estate && adminsMap.get(user.id)?.estates?.[estate.id] === true) {
            userInEstate = true;
            adminUsers.push(user);
          }
          if (user.userType === UserType.CREATED && user.contextEstateId === estate?.id) {
            userInEstate = true;
          }
          const userAccess: UserEstateAccessData | undefined = estateUserAccessMap.get(user.id);
          userInEstate ||= !!ObjHlp.get(userAccess, estate?.id ?? '');
          if (userInEstate) usersInEstate.push(user);
        });

        this._adminUsers$.next(adminUsers);
        this._usersInEstate$.next(usersInEstate);
      })
    );

    this.subscription$.add(
      combineLatest([this._context$, this._company$, this._estate$])
        .pipe(
          switchMap(([type, company, estate]) => {
            let contextData$: Observable<any[]>;
            if (type === ContextType.COMPANY && company) {
              contextData$ = this.companyService.getFeatures$().pipe(map((features) => [null, features, company.id]));
            } else if (type === ContextType.ESTATE && estate && company?.id === estate.companyId) {
              contextData$ = this.estateService.getFeatures$().pipe(map((features) => [estate, features, company.id]));
            } else return of([]);

            return contextData$.pipe(
              switchMap(([contextEstate, features, companyId]) =>
                combineLatest([
                  of(contextEstate),
                  of(features),
                  of(companyId),
                  this._compEmployee$,
                  this._superAdmins$,
                  this._estateAccess$
                ])
              )
            );
          })
        )
        .subscribe(([estate, features, companyId, employee, superAdmins, estateAccess]) => {
          if (!companyId) {
            this._contextPerms$.next(null);
            return;
          }
          const hasEstateAccess = !estate || estateAccess.includes(estate.id);
          const currentEmployee =
            employee?.companyId === companyId && employee?.id === this.peekCurrentUserId() && hasEstateAccess ? employee : null;
          const superAdmin = !!superAdmins?.includes(this.peekCurrentUserId());

          this._contextPerms$.next({
            isPrs: estate?.type === EstateType.PRS,
            feat: features ?? null,
            ePerm: currentEmployee?.permissions ?? {},
            canChat: !!estate && (superAdmin || currentEmployee?.permissions?.chat?.view === true),
            superAdmin
          });
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
    this.userIdSub$.unsubscribe();
  }

  /* eslint-disable max-len */
  public get isUserSuperAdmin():  boolean                             { return this._superAdmin;                     } // prettier-ignore
  public get currentEstate$():    Observable<EstateData | null>       { return this._estate$.asObservable();         } // prettier-ignore
  public get currentUser$():      Observable<UserData | null>         { return this._user$.asObservable();           } // prettier-ignore
  public get currentCompany$():   Observable<CompanyData | null>      { return this._company$.asObservable();        } // prettier-ignore
  public get userEstateAccess$(): Observable<string[]>                { return this._estateAccess$.asObservable();   } // prettier-ignore
  public get superAdminIds$():    Observable<string[]>                { return this._superAdmins$.asObservable();    } // prettier-ignore
  public get estateAdminUsers$(): Observable<UserData[]>              { return this._adminUsers$.asObservable();     } // prettier-ignore
  public get estateUserVMs$():    Observable<EstateUserVM[]>          { return this._estateUsersVM$.asObservable();  } // prettier-ignore
  public get customTheme$():      Observable<CompanyWebTheme | null>  { return this._theme$.asObservable();          } // prettier-ignore
  public get contextPerms$():     Observable<ContextPermVM | null>    { return this._contextPerms$.asObservable();   } // prettier-ignore
  public get breakData$():        Observable<BreakData | null>        { return this._appBreak$.asObservable();       } // prettier-ignore
  public get context$():          Observable<ContextType | null>      { return this._context$.asObservable();        } // prettier-ignore
  public get version$():          Observable<VersionNumber>           { return this._version$.asObservable();        } // prettier-ignore
  /* eslint-enable max-len */

  public get currentEmployee$(): Observable<CompanyEmployeeData | null> {
    return combineLatest([this._compEmployee$, this._company$, this._user$]).pipe(
      map(([employee, company, user]) => (employee?.companyId === company?.id && employee?.id === user?.id ? employee : null))
    );
  }

  public userEstateAccessOnce(): Promise<string[]> {
    return AsyncHlp.onFirst(this._estateAccess$, filter((x) => !!x)); // prettier-ignore
  }

  public currentEstateOnce(): Promise<EstateData | null> {
    return AsyncHlp.onFirst(this._estate$, filter((x) => !!x)); // prettier-ignore
  }

  /* eslint-disable max-len */
  public currentEstateFirst():     Promise<EstateData | null>           { return AsyncHlp.onFirst(this._estate$);       } // prettier-ignore
  public currentEmployeeFirst():   Promise<CompanyEmployeeData | null>  { return AsyncHlp.onFirst(this.currentEmployee$); } // prettier-ignore
  /* eslint-enable max-len */

  /**
   * Get all users in current estate (estate admins and resident users)
   * @return {Observable<UserData[]>}
   */
  public get usersInEstate$(): Observable<UserData[]> { return this._usersInEstate$.asObservable(); } // prettier-ignore

  /**
   * Get recent estate BehaviorSubject value
   * @return {EstateData | null}
   */
  public peekCurrentEstate(): EstateData | null { return this._estate$.value; } // prettier-ignore

  /**
   * Get recent estate's id or empty string
   * @return {string}
   */
  public peekCurrentEstateId(): string { return this.peekCurrentEstate()?.id ?? ''; } // prettier-ignore

  /**
   * Get recent company BehaviorSubject value
   * @return {CompanyData | null}
   */
  public peekCurrentCompany(): CompanyData | null { return this._company$.value; } // prettier-ignore

  /**
   * Get recent company's id or empty string
   * @return {string}
   */
  public peekCurrentCompanyId(): string { return this.peekCurrentCompany()?.id ?? ''; } // prettier-ignore

  /**
   * Peek all estate admin users in current estate
   * @return {UserData[]}
   */
  public peekEstateAdminUsers(): UserData[] { return this._adminUsers$.value; } // prettier-ignore

  /**
   * Get recent user BehaviorSubject value
   * @return {UserData | null}
   */
  public peekCurrentUser(): UserData | null { return this._user$.value; } // prettier-ignore

  /**
   * Get recent user's id or empty string
   * @return {string}
   */
  public peekCurrentUserId(): string { return this.peekCurrentUser()?.id ?? ''; } // prettier-ignore

  /**
   * Get recent app context BehaviorSubject value
   * @return {ContextType | null}
   */
  public peekContext(): ContextType | null { return this._context$.value; } // prettier-ignore

  /**
   * Get recent context permissions BehaviorSubject value
   * @return {ContextPermVM | null}
   */
  public peekPerms(): ContextPermVM | null { return this._contextPerms$.value; } // prettier-ignore

  /** @key userId */
  // eslint-disable-next-line max-len
  private get dbEstateUser() { return this.dbSrv.estateFactory.getCollection<UserAccessData>('estateUser/$eId'); } // prettier-ignore

  /** @key userId */
  private dbCompany = (id: string) => this.dbSrv.commonFactory.getObject<CompanyData>(`company/${id}`);
  private get dbSuperadmin() { return this.dbSrv.commonFactory.getCollection<string>('superadmin'); } // prettier-ignore
  private get dbAppBreak() { return this.dbSrv.commonFactory.getObject<BreakData>('appBreak/web'); } // prettier-ignore
  // eslint-disable-next-line max-len
  private get dbFlikoVersion() { return this.dbSrv.commonFactory.getObject<VersionNumber>('fliko/crmVersion'); } // prettier-ignore

  /**
   * Handle user change after login: check for passwd change, show what's new and save last login to employee
   * @param user
   * @param employee
   * @param isSuperadmin
   */
  private handleUserChange(user: UserData, employee: Nullable<EmployeeData>): void {
    const passwdExpireDate = TimeHlp.add(Date.now(), -RenewPasswordComponent.PERIOD_OF_VALIDITY, 'DAY');
    if (!user.providerId && (user.passwordSetDate ?? 0) < passwdExpireDate) {
      this.dialogSrv.openSimpleDialog(RenewPasswordComponent, user, { autoFocus: true, disableClose: true });
    }

    if (this.authSrv.hasLoggedAfterLoad()) {
      this.userService.setUsersActivity(user.id);
      if (employee && !this._superAdmin) {
        // ? Super admins don't have employees
        this.companyService.addEmployeeLogin(user.id, employee?.lastLogins?.length ?? 0, this.peekCurrentCompanyId());
      }
    }

    if ((user.lastActivity ?? 0) < this.versionSrv.getLastUpdateDate()) {
      this.dialogSrv.openSimpleDialog(WhatsNewComponent, null);
    }
  }
}
